<template>
  <section id="features" class="features section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title animate-slide-up">
          {{ $t('features.title') }}
        </h2>
        <p class="section-subtitle animate-slide-up">
          {{ $t('features.subtitle') }}
        </p>
      </div>
      
      <div class="features-grid">
        <div v-for="(feature, index) in features" :key="index" 
             class="feature-card card animate-slide-up"
             :style="{ animationDelay: `${index * 0.1}s` }">
          <div class="feature-header">
            <div class="feature-icon">
              <component :is="feature.icon" />
            </div>
            <h3 class="feature-title">{{ feature.title }}</h3>
          </div>
          <p class="feature-description">{{ feature.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { h, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Componentes de ícones simplificados
const DevelopmentIcon = () => h('svg', { 
  fill: 'none', 
  stroke: 'currentColor', 
  viewBox: '0 0 24 24',
  class: 'w-8 h-8'
}, [
  h('path', { 
    d: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
    'stroke-linecap': 'round', 
    'stroke-linejoin': 'round', 
    'stroke-width': '2'
  })
])

const ConsultingIcon = () => h('svg', { 
  fill: 'none', 
  stroke: 'currentColor', 
  viewBox: '0 0 24 24',
  class: 'w-8 h-8'
}, [
  h('path', { 
    d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    'stroke-linecap': 'round', 
    'stroke-linejoin': 'round', 
    'stroke-width': '2'
  })
])

const SupportIcon = () => h('svg', { 
  fill: 'none', 
  stroke: 'currentColor', 
  viewBox: '0 0 24 24',
  class: 'w-8 h-8'
}, [
  h('path', { 
    d: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z',
    'stroke-linecap': 'round', 
    'stroke-linejoin': 'round', 
    'stroke-width': '2'
  })
])



const features = computed(() => [
  {
    icon: DevelopmentIcon,
    title: t('features.development.title'),
    description: t('features.development.description')
  },
  {
    icon: ConsultingIcon,
    title: t('features.consulting.title'),
    description: t('features.consulting.description')
  },
  {
    icon: SupportIcon,
    title: t('features.support.title'),
    description: t('features.support.description')
  }
])


</script>

<style scoped>
.features {
  background: var(--background-secondary);
  position: relative;
}

.features::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border-medium), transparent);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-8);
}

.feature-card {
  position: relative;
  overflow: hidden;
}

.feature-header {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.feature-icon {
  width: 48px;
  height: 48px;
  background: var(--gradient-primary);
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-md);
}

.feature-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  line-height: var(--line-height-snug);
}

.feature-description {
  color: var(--text-secondary);
  line-height: var(--line-height-relaxed);
  font-size: var(--font-size-base);
}

@media (max-width: 768px) {
  .features-grid {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
  
  .feature-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }
  
  .feature-icon {
    width: 40px;
    height: 40px;
  }
  
  .feature-title {
    font-size: var(--font-size-lg);
  }
}
</style>
